import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from '@/App';
import { bootstrapDevDefaults, validateProductionConfig } from '@/lib/featureFlags';
import { logger } from '@/lib/logger';
import { initSentry } from '@/lib/sentry';
import '@/styles/globals.css';

// Initialize Sentry before anything else that could throw
initSentry();

// Catch unhandled promise rejections globally
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  logger.error('Unhandled promise rejection', event.reason);
});

// Reload once on stale-chunk failures after a deploy (Vite emits this on dynamic-import errors)
const CHUNK_RELOAD_FLAG = 'chunk-reloaded';
window.addEventListener('vite:preloadError', event => {
  event.preventDefault();
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1') {
    logger.error('Chunk preload failed after reload', event);
    return;
  }
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
  window.location.reload();
});
window.addEventListener('load', () => {
  sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
});

// CRITICAL: Validate production configuration before starting the app
// This will throw an error if mock data or other dev features are enabled in staging/production
validateProductionConfig();

// Seed localStorage with dev defaults (factory/tenant IDs) when auth is bypassed
bootstrapDevDefaults();

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
